// /src/components/Signup.js

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../api/axios';
import { 
  FaUserPlus, 
  FaSpinner, 
  FaUser, 
  FaLock,
  FaEnvelope,
  FaExclamationCircle
} from 'react-icons/fa';
import { Card, CardContent } from './ui/card';
import EmailVerification from './EmailVerification';

const PasswordStrengthIndicator = ({ password }) => {
  const { t } = useTranslation();
  
  const calculateStrength = (pwd) => {
    let strength = 0;
    if (pwd.length >= 8) strength++;
    if (/[A-Z]/.test(pwd)) strength++;
    if (/[0-9]/.test(pwd)) strength++;
    if (/[^A-Za-z0-9]/.test(pwd)) strength++;
    return strength;
  };

  const strength = calculateStrength(password);
  
  const getStrengthText = () => {
    switch (strength) {
      case 0: return t('auth.signup.passwordStrength.veryWeak');
      case 1: return t('auth.signup.passwordStrength.weak');
      case 2: return t('auth.signup.passwordStrength.medium');
      case 3: return t('auth.signup.passwordStrength.strong');
      case 4: return t('auth.signup.passwordStrength.veryStrong');
      default: return '';
    }
  };

  const getStrengthColor = () => {
    switch (strength) {
      case 0: return 'bg-red-500';
      case 1: return 'bg-orange-500';
      case 2: return 'bg-yellow-500';
      case 3: return 'bg-green-500';
      case 4: return 'bg-emerald-500';
      default: return 'bg-gray-500';
    }
  };

  return password ? (
    <div className="mt-2">
      <div className="flex justify-between mb-1">
        <span className="text-sm text-gray-400">
          {t('auth.signup.passwordStrength.title')}
        </span>
        <span className={`text-sm ${
          strength <= 1 ? 'text-red-400' : 
          strength === 2 ? 'text-yellow-400' : 
          'text-green-400'
        }`}>
          {getStrengthText()}
        </span>
      </div>
      <div className="h-1 w-full bg-gray-700 rounded-full">
        <div 
          className={`h-1 rounded-full transition-all duration-300 ${getStrengthColor()}`}
          style={{ width: `${(strength / 4) * 100}%` }}
        />
      </div>
    </div>
  ) : null;
};

const Signup = ({ setIsAuthenticated, setUserRole }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [verificationStep, setVerificationStep] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');

  const validateForm = () => {
    const newErrors = {};

    if (!formData.username) {
      newErrors.username = t('auth.signup.validation.usernameRequired');
    } else if (formData.username.length < 3) {
      newErrors.username = t('auth.signup.validation.usernameLength');
    }

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (!formData.password) {
      newErrors.password = t('auth.signup.validation.passwordRequired');
    } else if (formData.password.length < 8) {
      newErrors.password = t('auth.signup.validation.passwordLength');
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = t('auth.signup.validation.passwordMismatch');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
  
    if (!validateForm()) {
      setIsLoading(false);
      return;
    }
  
    try {
      const response = await api.post('/auth/signup', {
        username: formData.username,
        email: formData.email,
        password: formData.password
      });
  
      localStorage.setItem('userEmail', formData.email);
      setRegisteredEmail(formData.email);
      setVerificationStep(true);
    } catch (error) {
      setErrors({
        general: error.response?.data?.error || t('auth.signup.errors.generalError')
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerificationSuccess = () => {
    setIsAuthenticated(true);
    setUserRole('user');
  };

  if (verificationStep) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900 px-4">
        <EmailVerification 
          email={registeredEmail} 
          onVerificationSuccess={handleVerificationSuccess}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-900 px-4">
      <div className="w-full max-w-md space-y-8">
        <div className="text-center">
          <Link to="/">
            <h1 className="text-4xl font-bold text-white mb-2 hover:text-gray-200 transition-colors">
              {t('auth.common.brandName')}
            </h1>
          </Link>
          <p className="text-gray-400">{t('auth.signup.subtitle')}</p>
        </div>

        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              {errors.general && (
                <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-4 flex items-center">
                  <FaExclamationCircle className="text-red-500 mr-2" />
                  <span className="text-red-500">{errors.general}</span>
                </div>
              )}

              {/* Username Field */}
              <div>
                <label htmlFor="username" className="block text-sm font-medium text-gray-300 mb-1">
                  {t('auth.signup.form.username')}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaUser className="text-gray-500" />
                  </div>
                  <input
                    type="text"
                    name="username"
                    value={formData.username}
                    onChange={handleChange}
                    className="block w-full pl-10 pr-3 py-2 bg-gray-700 border border-gray-700 rounded-lg 
                             text-white placeholder-gray-400 focus:outline-none focus:border-indigo-500 
                             focus:ring-1 focus:ring-indigo-500"
                    placeholder={t('auth.signup.form.usernamePlaceholder')}
                  />
                </div>
                {errors.username && (
                  <p className="mt-1 text-sm text-red-500">{errors.username}</p>
                )}
              </div>

              {/* Email Field */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-1">
                  Email
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaEnvelope className="text-gray-500" />
                  </div>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="block w-full pl-10 pr-3 py-2 bg-gray-700 border border-gray-700 rounded-lg 
                             text-white placeholder-gray-400 focus:outline-none focus:border-indigo-500 
                             focus:ring-1 focus:ring-indigo-500"
                    placeholder="Enter your email"
                  />
                </div>
                {errors.email && (
                  <p className="mt-1 text-sm text-red-500">{errors.email}</p>
                )}
              </div>

              {/* Password Fields */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-1">
                  {t('auth.signup.form.password')}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaLock className="text-gray-500" />
                  </div>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className="block w-full pl-10 pr-3 py-2 bg-gray-700 border border-gray-700 rounded-lg 
                             text-white placeholder-gray-400 focus:outline-none focus:border-indigo-500 
                             focus:ring-1 focus:ring-indigo-500"
                    placeholder={t('auth.signup.form.passwordPlaceholder')}
                  />
                </div>
                {errors.password && (
                  <p className="mt-1 text-sm text-red-500">{errors.password}</p>
                )}
                <PasswordStrengthIndicator password={formData.password} />
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-300 mb-1">
                  {t('auth.signup.form.confirmPassword')}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaLock className="text-gray-500" />
                  </div>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className="block w-full pl-10 pr-3 py-2 bg-gray-700 border border-gray-700 rounded-lg 
                             text-white placeholder-gray-400 focus:outline-none focus:border-indigo-500 
                             focus:ring-1 focus:ring-indigo-500"
                    placeholder={t('auth.signup.form.confirmPasswordPlaceholder')}
                  />
                </div>
                {errors.confirmPassword && (
                  <p className="mt-1 text-sm text-red-500">{errors.confirmPassword}</p>
                )}
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex justify-center items-center px-4 py-2 border border-transparent 
                         rounded-lg shadow-sm text-sm font-medium text-white bg-indigo-600 
                         hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 
                         focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed 
                         transition-colors duration-200"
              >
                {isLoading ? (
                  <>
                    <FaSpinner className="animate-spin mr-2" />
                    {t('auth.signup.form.submitting')}
                  </>
                ) : (
                  <>
                    <FaUserPlus className="mr-2" />
                    {t('auth.signup.form.submit')}
                  </>
                )}
              </button>

              {/* Login Link */}
              <div className="text-center mt-4">
                <Link 
                  to="/login" 
                  className="text-indigo-400 hover:text-indigo-300 transition-colors duration-200"
                >
                  {t('auth.signup.links.login')}
                </Link>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Signup;